import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { mensajeDeError } from '@/lib/errores';
import { recibosDisponibles } from '@/lib/cobros';
import { confirmAsync } from '@/lib/confirm';
import { 
  FileText, 
  Download, 
  AlertCircle, 
  CheckCircle2, 
  Calendar,
  Banknote,
  Eye,
  Settings,
  BanknoteIcon,
  FileCheck,
  Loader2
} from 'lucide-react';

export default function SepaRemittances({ user }) {
  const [remittances, setRemittances] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [creditor, setCreditor] = useState(null);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [creditorDialogOpen, setCreditorDialogOpen] = useState(false);
  
  // Form data
  const [collectionDate, setCollectionDate] = useState('');
  // Rango de fecha de EMISIÓN. Las remesas no se mandan al banco de golpe: van
  // por tandas de fecha de emisión, y según la fecha se mandan un día u otro.
  // Sin esto, "marcar todas" juntaba tandas distintas en la misma remesa.
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  // El detalle de las excluidas nace plegado: lo que hace falta leer es
  // cuántas y por qué, no la lista entera.
  const [verExcluidas, setVerExcluidas] = useState(false);
  // Cuál se está presentando ahora mismo: el botón se bloquea solo en esa
  // fila, no en todas.
  const [presentando, setPresentando] = useState(null);
  // Con 257 facturas, la vista previa y la generación tardan lo suyo: el
  // servidor consulta la ficha y el mandato de cada una, y la generación
  // además escribe 257 adeudos y construye el XML.
  //
  // Sin señal de que está trabajando, el botón parece muerto y se vuelve a
  // pulsar. En la generación eso no es solo molesto: dos peticiones a la vez
  // pueden reservar dos números de remesa y partir el cobro en dos ficheros.
  const [generandoPreview, setGenerandoPreview] = useState(false);
  const [generandoRemesa, setGenerandoRemesa] = useState(false);
  const [notes, setNotes] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [selectedRemittance, setSelectedRemittance] = useState(null);
  
  // Creditor form
  const [creditorForm, setCreditorForm] = useState({
    creditor_name: '',
    creditor_id: '',
    creditor_iban: '',
    creditor_bic: ''
  });

  useEffect(() => {
    fetchRemittances();
    fetchCreditor();
  }, []);

  const fetchRemittances = async () => {
    try {
      const response = await axiosInstance.get('/sepa/remittances');
      setRemittances(response.data);
    } catch (error) {
      toast.error('Error al cargar remesas');
    }
  };

  const fetchCreditor = async () => {
    try {
      const response = await axiosInstance.get('/sepa/creditor');
      if (response.data) {
        setCreditor(response.data);
        setCreditorForm({
          creditor_name: response.data.creditor_name,
          creditor_id: response.data.creditor_id,
          creditor_iban: response.data.creditor_iban,
          creditor_bic: response.data.creditor_bic
        });
      }
    } catch (error) {
      console.error('Error loading creditor:', error);
    }
  };

  const fetchAcceptedInvoices = async (rango = {}) => {
    try {
      // El rango se filtra en el SERVIDOR, no aquí: la lista trae hasta 1.000
      // facturas y filtrar en el navegador dejaría fuera lo que no llegue.
      // Sin plantillas repetitivas: programan facturas, no son recibos.
      const params = new URLSearchParams({ status: 'accepted', is_recurring: 'false' });
      if (rango.desde) params.set('desde', rango.desde);
      if (rango.hasta) params.set('hasta', rango.hasta);

      const response = await axiosInstance.get(`/invoices?${params}`);
      const availableInvoices = recibosDisponibles(response.data);
      setInvoices(availableInvoices);
      // Lo que estuviera marcado y ya no salga en la lista deja de estarlo: si
      // no, una factura fuera del rango seguiría entrando en la remesa sin
      // verse por ninguna parte.
      setSelectedInvoices((antes) =>
        antes.filter((id) => availableInvoices.some((inv) => inv.id === id)));
    } catch (error) {
      toast.error(mensajeDeError(error, 'Error al cargar facturas'));
    }
  };

  /** Cambiar una de las dos fechas recarga la lista con el rango nuevo. */
  const cambiarRango = (campo, valor) => {
    const rango = { desde, hasta, [campo]: valor };
    if (campo === 'desde') setDesde(valor); else setHasta(valor);
    fetchAcceptedInvoices(rango);
  };

  const handleOpenCreateDialog = () => {
    if (!creditor) {
      toast.error('Debes configurar los datos del acreedor primero');
      setCreditorDialogOpen(true);
      return;
    }
    
    setDesde('');
    setHasta('');
    fetchAcceptedInvoices();
    setSelectedInvoices([]);
    setCollectionDate('');
    setNotes('');
    setCreateDialogOpen(true);
  };

  const handleToggleInvoice = (invoiceId) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    } else {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === invoices.length) {
      // Deselect all
      setSelectedInvoices([]);
    } else {
      // Select all
      setSelectedInvoices(invoices.map(inv => inv.id));
    }
  };

  const handlePreview = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('Selecciona al menos una factura');
      return;
    }

    if (!collectionDate) {
      toast.error('Selecciona una fecha de cobro');
      return;
    }

    // Guarda contra el doble clic: el `disabled` del botón tarda un frame en
    // aplicarse y dos pulsaciones seguidas pueden colarse las dos.
    if (generandoPreview) return;
    setGenerandoPreview(true);
    try {
      const response = await axiosInstance.post('/sepa/remittances/preview', {
        invoice_ids: selectedInvoices,
        collection_date: collectionDate
      });

      setPreviewData(response.data);
      setVerExcluidas(false);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error(mensajeDeError(error, 'Error al generar vista previa'));
    } finally {
      setGenerandoPreview(false);
    }
  };

  const handleGenerate = async () => {
    // Lo mismo, pero aquí importa de verdad: dos peticiones a la vez reservan
    // dos números de remesa y parten el cobro en dos ficheros.
    if (generandoRemesa) return;
    setGenerandoRemesa(true);
    try {
      const response = await axiosInstance.post('/sepa/remittances/generate', 
        selectedInvoices,
        {
          params: {
            collection_date: collectionDate,
            notes: notes || undefined
          }
        }
      );
      
      // Lo que se ha quedado fuera se avisa aparte y sin prisa. Un "remesa
      // generada correctamente" a secas escondia que faltaban cobros dentro:
      // no se descubria hasta que esos clientes no pagaban.
      const fuera = response.data?.excluidas || [];
      toast.success(response.data?.message || 'Remesa generada correctamente');
      if (fuera.length > 0) {
        toast.warning(
          `${fuera.length} factura(s) NO se cobrarán: ` +
          fuera.slice(0, 3).map((e) => `${e.factura || e.invoice_id} (${e.motivo})`).join('; ') +
          (fuera.length > 3 ? ` y ${fuera.length - 3} más` : ''),
          { duration: 15000 }
        );
      }
      setCreateDialogOpen(false);
      setPreviewDialogOpen(false);
      fetchRemittances();
    } catch (error) {
      toast.error(mensajeDeError(error, 'Error al generar remesa'));
    } finally {
      setGenerandoRemesa(false);
    }
  };

  const handleViewDetail = async (remittanceId) => {
    try {
      const response = await axiosInstance.get(`/sepa/remittances/${remittanceId}`);
      setSelectedRemittance(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalle de remesa');
    }
  };

  /**
   * La remesa ya está en el banco: se marca presentada y sus recibos pasan a
   * pagados, con la FECHA DE COBRO de la remesa (Angel, 22-sep-2026).
   *
   * Se pregunta antes porque no se puede deshacer desde la pantalla: a
   * partir de aquí esas facturas constan cobradas, y lo que las saca de ahí
   * es marcar el recibo como devuelto una a una.
   */
  const handlePresentar = async (remittance) => {
    const cobro = remittance.collection_date
      ? new Date(remittance.collection_date).toLocaleDateString('es-ES')
      : 'la fecha de cobro';
    const ok = await confirmAsync(
      `¿Marcar ${remittance.remittance_id} como presentada al banco?\n\n`
      + `Sus ${remittance.total_debits || 0} recibos pasarán a PAGADOS con fecha ${cobro}. `
      + 'No se puede deshacer desde aquí.');
    if (!ok) return;

    setPresentando(remittance.id);
    try {
      const { data } = await axiosInstance.post(
        `/sepa/remittances/${remittance.id}/present`, {});
      toast.success(data.message);
      fetchRemittances();
    } catch (error) {
      toast.error(mensajeDeError(error));
    } finally {
      setPresentando(null);
    }
  };

  const handleDownloadXML = async (remittanceId, filename) => {
    try {
      const response = await axiosInstance.get(`/sepa/remittances/${remittanceId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('XML descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar XML');
    }
  };

  const handleSaveCreditor = async () => {
    try {
      await axiosInstance.post('/sepa/creditor', null, {
        params: creditorForm
      });
      
      toast.success('Configuración guardada correctamente');
      setCreditorDialogOpen(false);
      fetchCreditor();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'Borrador', class: 'bg-gray-100 text-gray-800' },
      generated: { label: 'Generada', class: 'bg-blue-100 text-blue-800' },
      sent: { label: 'Enviada', class: 'bg-green-100 text-green-800' },
      processed: { label: 'Procesada', class: 'bg-green-600 text-white' },
      rejected: { label: 'Rechazada', class: 'bg-red-100 text-red-800' }
    };
    return config[status] || config.draft;
  };

  const totalSelected = selectedInvoices.reduce((sum, id) => {
    const invoice = invoices.find(inv => inv.id === id);
    return sum + (invoice?.total || 0);
  }, 0);

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#212B35]">Remesas SEPA</h1>
            <p className="text-gray-600">Gestión de recibos domiciliados bancarios</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCreditorDialogOpen(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Configurar Acreedor
            </Button>
            <Button
              onClick={handleOpenCreateDialog}
              className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              Nueva Remesa
            </Button>
          </div>
        </div>

        {/* Creditor Warning */}
        {!creditor && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-orange-900">Configuración requerida</h3>
                  <p className="text-sm text-orange-800 mt-1">
                    Debes configurar los datos del acreedor (CI SEPA, IBAN, BIC) antes de generar remesas.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 border-orange-300"
                    onClick={() => setCreditorDialogOpen(true)}
                  >
                    Configurar ahora
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Remittances List */}
        <div className="grid gap-4">
          {remittances.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <BanknoteIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No hay remesas generadas</p>
                <p className="text-sm mt-2">Crea tu primera remesa SEPA para cobrar facturas</p>
              </CardContent>
            </Card>
          ) : (
            remittances.map((remittance) => {
              const statusBadge = getStatusBadge(remittance.status);
              
              return (
                <Card key={remittance.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg">{remittance.remittance_id}</h3>
                          <Badge className={statusBadge.class}>
                            {statusBadge.label}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Fecha de cobro</p>
                            <p className="font-semibold">
                              {new Date(remittance.collection_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Total adeudos</p>
                            <p className="font-semibold">{remittance.total_debits}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Importe total</p>
                            <p className="font-semibold text-lg text-[#9C0F17]">
                              {remittance.total_amount.toFixed(2)}€
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Creada por</p>
                            <p className="font-semibold">{remittance.created_by_name}</p>
                          </div>
                        </div>

                        {remittance.notes && (
                          <p className="text-sm text-gray-600 mt-2">
                            <strong>Notas:</strong> {remittance.notes}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetail(remittance.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver Detalle
                        </Button>
                        {remittance.xml_filename && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleDownloadXML(remittance.id, remittance.xml_filename)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Descargar XML
                          </Button>
                        )}
                        {/* Generar la remesa y presentarla son dos cosas: el
                            fichero se puede revisar y volver a generar;
                            presentarlo pasa una vez y da por cobrados sus
                            recibos. Por eso el botón solo sale mientras no
                            esté presentada. */}
                        {remittance.status !== 'sent' && remittance.status !== 'processed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePresentar(remittance)}
                            disabled={presentando === remittance.id}
                            className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white"
                            data-testid="marcar-presentada"
                          >
                            <Banknote className="w-4 h-4 mr-1" />
                            {presentando === remittance.id ? 'Marcando...' : 'Marcar presentada'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Create Remittance Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nueva Remesa SEPA</DialogTitle>
              <DialogDescription>
                Selecciona las facturas aceptadas que deseas incluir en la remesa
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* El rango de emisión va ARRIBA del todo, antes que nada: es lo
                  primero que se decide, porque determina qué facturas se
                  ofrecen siquiera. */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <Label className="font-semibold">Facturas emitidas entre</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label htmlFor="desde" className="text-xs text-slate-500">Desde</Label>
                    <Input
                      id="desde"
                      type="date"
                      value={desde}
                      max={hasta || undefined}
                      onChange={(e) => cambiarRango('desde', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="hasta" className="text-xs text-slate-500">Hasta</Label>
                    <Input
                      id="hasta"
                      type="date"
                      value={hasta}
                      min={desde || undefined}
                      onChange={(e) => cambiarRango('hasta', e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Los dos días entran. Déjalos en blanco para ver todas las
                  pendientes.{' '}
                  {(desde || hasta) && (
                    <span className="font-medium text-slate-700">
                      Ahora se ven {invoices.length}.
                    </span>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="collection_date">Fecha de Cobro *</Label>
                  <Input
                    id="collection_date"
                    type="date"
                    value={collectionDate}
                    onChange={(e) => setCollectionDate(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Fecha en la que se realizará el cobro
                  </p>
                </div>
                <div>
                  <Label>Facturas Seleccionadas</Label>
                  <div className="text-2xl font-bold text-[#9C0F17]">
                    {selectedInvoices.length} facturas - {totalSelected.toFixed(2)}€
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notas (opcional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notas internas sobre esta remesa..."
                  rows={2}
                />
              </div>

              <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-4">
                    <h4 className="font-semibold">Facturas Disponibles ({invoices.length})</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                      onCheckedChange={handleSelectAll}
                      id="select-all"
                    />
                    <Label htmlFor="select-all" className="cursor-pointer text-sm">
                      Seleccionar todas
                    </Label>
                  </div>
                </div>
                
                {invoices.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No hay facturas aceptadas disponibles
                  </p>
                ) : (
                  <div className="space-y-2">
                    {invoices.map((invoice) => {
                      const isSelected = selectedInvoices.includes(invoice.id);
                      
                      return (
                        <div
                          key={invoice.id}
                          className={`flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-gray-50 ${
                            isSelected ? 'border-green-500 bg-green-50' : ''
                          }`}
                          onClick={() => handleToggleInvoice(invoice.id)}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleToggleInvoice(invoice.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold">{invoice.invoice_number}</span>
                              <span className="text-sm text-gray-600">
                                Vence: {new Date(invoice.due_date).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              Cliente: {invoice.customer_fiscal_name || invoice.customer_name || 'Sin nombre'}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{invoice.total.toFixed(2)}€</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handlePreview} 
                  disabled={selectedInvoices.length === 0 || !collectionDate || generandoPreview}
                  className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white disabled:bg-gray-300"
                >
                  {generandoPreview ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Comprobando {selectedInvoices.length} facturas...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Vista Previa
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Vista Previa de Remesa</DialogTitle>
              <DialogDescription>
                Revisa los datos antes de generar el archivo XML
              </DialogDescription>
            </DialogHeader>

            {previewData && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Fecha de cobro</p>
                    <p className="font-bold">{previewData.collection_date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total facturas</p>
                    <p className="font-bold">{previewData.total_invoices}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Importe total</p>
                    <p className="font-bold text-lg text-[#9C0F17]">
                      {previewData.total_amount.toFixed(2)}€
                    </p>
                  </div>
                </div>

                {/* Lo que NO entra en la remesa.
                    No son errores y no van en rojo: son facturas que no se
                    cobran por recibo -- las paga el cliente por su cuenta, van
                    por transferencia o son de importe cero. Llamarlas "Errores
                    de Validación" hacía pensar que algo estaba roto.

                    Y va PLEGADO y agrupado por motivo. Con 23 líneas sueltas,
                    el aviso empujaba fuera de la pantalla la tabla y los
                    botones: no se podía ni generar la remesa. */}
                {previewData.errors && previewData.errors.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-4">
                      <button
                        type="button"
                        onClick={() => setVerExcluidas(!verExcluidas)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <span className="font-semibold text-amber-900 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {previewData.errors.length} factura(s) no entran en la remesa
                        </span>
                        <span className="text-xs text-amber-700 underline">
                          {verExcluidas ? 'ocultar' : 'ver cuáles'}
                        </span>
                      </button>

                      {/* El resumen por motivo cabe siempre: es lo que hay que
                          leer. El detalle, solo si se pide. */}
                      <ul className="text-sm text-amber-900 mt-2 space-y-1">
                        {Object.entries(
                          previewData.errors.reduce((cuenta, e) => {
                            const m = e.motivo || 'sin motivo';
                            cuenta[m] = (cuenta[m] || 0) + 1;
                            return cuenta;
                          }, {})
                        ).map(([motivo, n]) => (
                          <li key={motivo}>• <strong>{n}</strong> — {motivo}</li>
                        ))}
                      </ul>

                      {verExcluidas && (
                        <div className="mt-3 max-h-40 overflow-y-auto border-t border-amber-200 pt-2">
                          <table className="w-full text-xs text-amber-900">
                            <tbody>
                              {previewData.errors.map((e, i) => (
                                <tr key={i} className="border-b border-amber-100">
                                  <td className="py-1 pr-2 font-mono">{e.factura}</td>
                                  <td className="py-1 pr-2">{e.cliente}</td>
                                  <td className="py-1 pr-2 text-right">
                                    {Number(e.importe || 0).toFixed(2)}€
                                  </td>
                                  <td className="py-1 text-amber-700">{e.motivo}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Factura</th>
                        <th className="p-2 text-left">Cliente</th>
                        <th className="p-2 text-left">IBAN</th>
                        <th className="p-2 text-right">Importe</th>
                        <th className="p-2 text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.items.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-2 font-mono">{item.invoice_number}</td>
                          <td className="p-2">{item.customer_name}</td>
                          <td className="p-2 font-mono text-xs">{item.iban}</td>
                          <td className="p-2 text-right font-semibold">{item.amount.toFixed(2)}€</td>
                          <td className="p-2 text-center">
                            {item.valid ? (
                              <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-600 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" disabled={generandoRemesa}
                          onClick={() => setPreviewDialogOpen(false)}>
                    Volver
                  </Button>
                  <Button
                    onClick={handleGenerate}
                    disabled={previewData.items.length === 0 || generandoRemesa}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {generandoRemesa ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generando la remesa... no cierres esta ventana
                      </>
                    ) : (
                      <>
                        <FileCheck className="w-4 h-4 mr-2" />
                        Generar Remesa XML
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalle de Remesa</DialogTitle>
              {selectedRemittance && (
                <DialogDescription>
                  {selectedRemittance.remittance_id} - {selectedRemittance.total_debits} adeudos
                </DialogDescription>
              )}
            </DialogHeader>

            {selectedRemittance && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Fecha de creación</p>
                    <p className="font-semibold">
                      {new Date(selectedRemittance.creation_date).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Fecha de cobro</p>
                    <p className="font-semibold">
                      {new Date(selectedRemittance.collection_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total adeudos</p>
                    <p className="font-semibold">{selectedRemittance.total_debits}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Importe total</p>
                    <p className="font-bold text-xl text-[#9C0F17]">
                      {selectedRemittance.total_amount.toFixed(2)}€
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Adeudos</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#9C0F17] text-white">
                        <tr>
                          <th className="p-2 text-left">Factura</th>
                          <th className="p-2 text-left">Cliente</th>
                          <th className="p-2 text-left">IBAN</th>
                          <th className="p-2 text-left">Mandato</th>
                          <th className="p-2 text-right">Importe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRemittance.debits?.map((debit, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-2 font-mono">{debit.invoice_number}</td>
                            <td className="p-2">{debit.customer_name}</td>
                            <td className="p-2 font-mono text-xs">{debit.debtor_iban}</td>
                            <td className="p-2 font-mono text-xs">{debit.mandate_id}</td>
                            <td className="p-2 text-right font-semibold">
                              {debit.amount.toFixed(2)}€
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Creditor Configuration Dialog */}
        <Dialog open={creditorDialogOpen} onOpenChange={setCreditorDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Configuración del Acreedor SEPA</DialogTitle>
              <DialogDescription>
                Datos de tu empresa para generar remesas SEPA
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="creditor_name">Nombre Fiscal de la Empresa *</Label>
                <Input
                  id="creditor_name"
                  value={creditorForm.creditor_name}
                  onChange={(e) => setCreditorForm({ ...creditorForm, creditor_name: e.target.value })}
                  placeholder="EMPRESA EJEMPLO SL"
                />
              </div>

              <div>
                <Label htmlFor="creditor_id">Identificador SEPA (CI) *</Label>
                <Input
                  id="creditor_id"
                  value={creditorForm.creditor_id}
                  onChange={(e) => setCreditorForm({ ...creditorForm, creditor_id: e.target.value })}
                  placeholder="ESxxZZZxxxxxxxxx"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formato: ESxxZZZxxxxxxxxx (proporcionado por tu banco)
                </p>
              </div>

              <div>
                <Label htmlFor="creditor_iban">IBAN de Cobro *</Label>
                <Input
                  id="creditor_iban"
                  value={creditorForm.creditor_iban}
                  onChange={(e) => setCreditorForm({ ...creditorForm, creditor_iban: e.target.value })}
                  placeholder="ES12 3456 7890 1234 5678 9012"
                />
              </div>

              <div>
                <Label htmlFor="creditor_bic">BIC del Banco *</Label>
                <Input
                  id="creditor_bic"
                  value={creditorForm.creditor_bic}
                  onChange={(e) => setCreditorForm({ ...creditorForm, creditor_bic: e.target.value })}
                  placeholder="SANTESMMXXX"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Banco Santander: SANTESMMXXX
                </p>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setCreditorDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveCreditor} className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white">
                  Guardar Configuración
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
